import React, { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button, Col, Row } from 'reactstrap';
import { Translate, ValidatedField, ValidatedForm, translate } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { convertDateTimeFromServer, convertDateTimeToServer, displayDefaultDateTime } from 'app/shared/util/date-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities as getMerchants } from 'app/entities/merchant/merchant.reducer';
import { getEntities as getCustomers } from 'app/entities/customer/customer.reducer';
import { UserCashBackStatus } from 'app/shared/model/enumerations/user-cash-back-status.model';
import { createEntity, getEntity, reset, updateEntity } from './user-cash-back.reducer';

export const UserCashBackUpdate = () => {
  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const { id } = useParams<'id'>();
  const isNew = id === undefined;

  const merchants = useAppSelector(state => state.merchant.entities);
  const customers = useAppSelector(state => state.customer.entities);
  const userCashBackEntity = useAppSelector(state => state.userCashBack.entity);
  const loading = useAppSelector(state => state.userCashBack.loading);
  const updating = useAppSelector(state => state.userCashBack.updating);
  const updateSuccess = useAppSelector(state => state.userCashBack.updateSuccess);
  const userCashBackStatusValues = Object.keys(UserCashBackStatus);

  const handleClose = () => {
    navigate(`/user-cash-back${location.search}`);
  };

  useEffect(() => {
    if (isNew) {
      dispatch(reset());
    } else {
      dispatch(getEntity(id));
    }

    dispatch(getMerchants({}));
    dispatch(getCustomers({}));
  }, []);

  useEffect(() => {
    if (updateSuccess) {
      handleClose();
    }
  }, [updateSuccess]);

  const saveEntity = values => {
    if (values.totalCashBack !== undefined && typeof values.totalCashBack !== 'number') {
      values.totalCashBack = Number(values.totalCashBack);
    }
    if (values.receiptAmount !== undefined && typeof values.receiptAmount !== 'number') {
      values.receiptAmount = Number(values.receiptAmount);
    }
    values.receiptDate = convertDateTimeToServer(values.receiptDate);
    values.createdAt = convertDateTimeToServer(values.createdAt);

    const entity = {
      ...userCashBackEntity,
      ...values,
      merchant: merchants.find(it => it.id.toString() === values.merchant?.toString()),
      customer: customers.find(it => it.id.toString() === values.customer?.toString()),
    };

    if (isNew) {
      dispatch(createEntity(entity));
    } else {
      dispatch(updateEntity(entity));
    }
  };

  const defaultValues = () =>
    isNew
      ? {
          receiptDate: displayDefaultDateTime(),
          createdAt: displayDefaultDateTime(),
        }
      : {
          status: 'PENDING',
          ...userCashBackEntity,
          receiptDate: convertDateTimeFromServer(userCashBackEntity.receiptDate),
          createdAt: convertDateTimeFromServer(userCashBackEntity.createdAt),
          merchant: userCashBackEntity?.merchant?.id,
          customer: userCashBackEntity?.customer?.id,
        };

  return (
    <div>
      <Row className="justify-content-center">
        <Col md="8">
          <h2 id="tycheApp.userCashBack.home.createOrEditLabel" data-cy="UserCashBackCreateUpdateHeading">
            <Translate contentKey="tycheApp.userCashBack.home.createOrEditLabel">Create or edit a UserCashBack</Translate>
          </h2>
        </Col>
      </Row>
      <Row className="justify-content-center">
        <Col md="8">
          {loading ? (
            <p>Loading...</p>
          ) : (
            <ValidatedForm defaultValues={defaultValues()} onSubmit={saveEntity}>
              {!isNew ? (
                <ValidatedField
                  name="id"
                  required
                  readOnly
                  id="user-cash-back-id"
                  label={translate('tycheApp.userCashBack.id')}
                  validate={{ required: true }}
                />
              ) : null}
              <ValidatedField
                label={translate('tycheApp.userCashBack.totalCashBack')}
                id="user-cash-back-totalCashBack"
                name="totalCashBack"
                data-cy="totalCashBack"
                type="text"
              />
              <ValidatedField
                label={translate('tycheApp.userCashBack.filePath')}
                id="user-cash-back-filePath"
                name="filePath"
                data-cy="filePath"
                type="text"
              />
              <ValidatedField
                label={translate('tycheApp.userCashBack.remark')}
                id="user-cash-back-remark"
                name="remark"
                data-cy="remark"
                type="text"
              />
              <ValidatedField
                label={translate('tycheApp.userCashBack.receiptId')}
                id="user-cash-back-receiptId"
                name="receiptId"
                data-cy="receiptId"
                type="text"
              />
              <ValidatedField
                label={translate('tycheApp.userCashBack.receiptAmount')}
                id="user-cash-back-receiptAmount"
                name="receiptAmount"
                data-cy="receiptAmount"
                type="text"
              />
              <ValidatedField
                label={translate('tycheApp.userCashBack.receiptDate')}
                id="user-cash-back-receiptDate"
                name="receiptDate"
                data-cy="receiptDate"
                type="datetime-local"
                placeholder="YYYY-MM-DD HH:mm"
              />
              <ValidatedField
                label={translate('tycheApp.userCashBack.errorMessage')}
                id="user-cash-back-errorMessage"
                name="errorMessage"
                data-cy="errorMessage"
                type="text"
              />
              <ValidatedField
                label={translate('tycheApp.userCashBack.createdAt')}
                id="user-cash-back-createdAt"
                name="createdAt"
                data-cy="createdAt"
                type="datetime-local"
                placeholder="YYYY-MM-DD HH:mm"
              />
              <ValidatedField
                label={translate('tycheApp.userCashBack.status')}
                id="user-cash-back-status"
                name="status"
                data-cy="status"
                type="select"
              >
                {userCashBackStatusValues.map(userCashBackStatus => (
                  <option value={userCashBackStatus} key={userCashBackStatus}>
                    {translate(`tycheApp.UserCashBackStatus.${userCashBackStatus}`)}
                  </option>
                ))}
              </ValidatedField>
              <ValidatedField
                id="user-cash-back-merchant"
                name="merchant"
                data-cy="merchant"
                label={translate('tycheApp.userCashBack.merchant')}
                type="select"
              >
                <option value="" key="0" />
                {merchants
                  ? merchants.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <ValidatedField
                id="user-cash-back-customer"
                name="customer"
                data-cy="customer"
                label={translate('tycheApp.userCashBack.customer')}
                type="select"
              >
                <option value="" key="0" />
                {customers
                  ? customers.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <Button tag={Link} id="cancel-save" data-cy="entityCreateCancelButton" to="/user-cash-back" replace color="info">
                <FontAwesomeIcon icon="arrow-left" />
                &nbsp;
                <span className="d-none d-md-inline">
                  <Translate contentKey="entity.action.back">Back</Translate>
                </span>
              </Button>
              &nbsp;
              <Button color="primary" id="save-entity" data-cy="entityCreateSaveButton" type="submit" disabled={updating}>
                <FontAwesomeIcon icon="save" />
                &nbsp;
                <Translate contentKey="entity.action.save">Save</Translate>
              </Button>
            </ValidatedForm>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default UserCashBackUpdate;
